import { ButtonMenuProps, MenuItem, useModal } from "@homebound/beam";
import { useLocation, useNavigate } from "react-router-dom";
import { createDevelopmentReviewTemplatesUrl } from "src/RouteUrls";
import { ItemTemplateStatus } from "src/generated/graphqlTypes";
import {
  useCreateNewItemTemplateVersionsMutation,
  useDeleteItemTemplatesMutation,
} from "src/routes/developments/templates/DevelopmentScopeTemplatePage.generated";
import { DevelopmentScopeTemplateFragment } from "src/routes/developments/templates/DevelopmentScopeTemplates.generated";
import { ArchiveItemTemplatesModal } from "src/routes/developments/templates/components/ArchiveItemTemplatesModal";
import { RenderMode } from "../review-and-publish/components/utils";
import { AddItemTemplatesReasonCodesModal } from "./AddItemTemplatesReasonCodesModal";

type DevelopmentScopeTemplateButtonMenuProps = {
  developmentId: string;
  itemTemplates: Pick<DevelopmentScopeTemplateFragment, "id" | "statusDetail" | "canCreateNewVersion">[];
  tableRefetch: () => void;
};

/** Bulk actions for the selected templates, rendered as the table's `kind: "menu"` header action. */
export function useDevelopmentScopeTemplateMenu(props: DevelopmentScopeTemplateButtonMenuProps): ButtonMenuProps {
  const navigate = useNavigate();
  const location = useLocation();
  const { developmentId, itemTemplates, tableRefetch } = props;
  const [createNewItemTemplateVersions] = useCreateNewItemTemplateVersionsMutation();
  const [deleteItemTemplates] = useDeleteItemTemplatesMutation();
  const { openModal } = useModal();
  const activeTemplates = itemTemplates.filter((it) => it.statusDetail?.code === ItemTemplateStatus.Active).nonEmpty;
  const draftTemplates = itemTemplates.filter((it) => it.statusDetail?.code === ItemTemplateStatus.Draft).nonEmpty;

  function cannotCreateNewVersions() {
    return !itemTemplates.every((it) => it.canCreateNewVersion.allowed) && "Active templates only";
  }

  function cannotEdit() {
    return (
      (activeTemplates && "Only draft templates can be edited") ||
      (itemTemplates.length < 2 && "Multiple templates must be selected")
    );
  }

  function cannotReviewAndPublish() {
    return !(draftTemplates && !activeTemplates) && showReviewAndPublishTooltip();
  }

  function cannotApplyToProjects() {
    return !(activeTemplates && !draftTemplates) && "Active templates only";
  }

  function canOpenMenuAction() {
    return itemTemplates.isEmpty;
  }

  function showReviewAndPublishTooltip() {
    return draftTemplates
      ? "Draft whole house and options templates must be selected together. Select the draft whole house temxwplate to Review & Publish"
      : "Selected templates have already been published";
  }

  function cannotDeleteDraftTemplates() {
    return !(draftTemplates && !activeTemplates) && "Draft templates only";
  }

  function maybeArchive() {
    return draftTemplates && "Active templates only";
  }

  const itemTemplatesIds = itemTemplates.map((it) => it.id);

  const menuItems: MenuItem[] = [
    {
      label: "Create New Versions",
      onClick: async () => {
        // Creates new versions of the selected templates
        await createNewItemTemplateVersions({ variables: { templateIds: itemTemplates.map(({ id }) => id) } });
        tableRefetch();
      },
      disabled: cannotCreateNewVersions(),
    },
    {
      label: "Edit",
      onClick: async () => {
        navigate(createDevelopmentReviewTemplatesUrl(developmentId), {
          state: { itemTemplatesIds, mode: RenderMode.edit, fromEditAction: true },
        });
      },
      disabled: cannotEdit(),
    },
    {
      label: "Review & Publish",
      onClick: () =>
        openModal({
          content: (
            <AddItemTemplatesReasonCodesModal
              itemTemplateIds={itemTemplatesIds}
              reviewUrl={createDevelopmentReviewTemplatesUrl(developmentId)}
            />
          ),
        }),
      disabled: cannotReviewAndPublish(),
    },
    {
      label: "Apply to Lots",
      onClick: () => {
        navigate(createDevelopmentReviewTemplatesUrl(developmentId), {
          state: { itemTemplatesIds: itemTemplates.map(({ id }) => id), mode: RenderMode.read, from: location },
        });
      },
      disabled: cannotApplyToProjects(),
    },
    {
      label: "Delete Drafts",
      onClick: async () => {
        // Deletes the selected templates
        await deleteItemTemplates({ variables: { id: itemTemplates.map(({ id }) => id) } });
        // We refetch as another version of the template may have become 'latest'
        tableRefetch();
      },
      disabled: cannotDeleteDraftTemplates(),
    },
  ];

  return {
    trigger: { label: "Actions" },
    items: menuItems,
    persistentItems: [
      {
        label: "Archive",
        onClick: () =>
          openModal({
            content: <ArchiveItemTemplatesModal itemTemplates={itemTemplates} onSuccess={() => tableRefetch()} />,
          }),
        disabled: maybeArchive(),
      },
    ],
    disabled: canOpenMenuAction(),
  };
}
